// Modelos de vehículo para el mapa (estilo Tesla/Uber): vista cenital que gira
// con el rumbo + carátula (vista 3/4) para tarjetas y el selector.
//
// Catálogo fijo, versionado con el código: los archivos los genera
// scripts/build-vehicle-models.sh desde lo que entrega diseño y viven en
// public/vehicles/<id>/{top,side}.webp. El vehículo sólo guarda el id
// (vehicle.pointer_model). `aspect` = alto/ancho de top.webp (lo imprime el
// script); `lengthM` = largo real aproximado, para que un tráiler se vea más
// largo que un auto.

export type VehicleCategory = "tracto" | "tracto-remolque" | "camion" | "pickup" | "auto" | "maquinaria" | "nostalgia";

export interface VehicleModel {
  id: string;
  name: string;
  category: VehicleCategory;
  lengthM: number;
  aspect: number;
  /** Pixel art (8-bit de v2): se dibuja con image-rendering: pixelated. */
  pixel?: boolean;
  /** Tiene vista trasera (rear.webp) para la vista de manejo; sin ella se usa top.webp acostado en la calle. */
  rear?: boolean;
}

// "Clásico" = el marcador de siempre (flecha/círculo por estado).
export const CLASSIC_MODEL_ID = "clasico";
// Marcadores de plataformas anteriores, para quien está acostumbrado a ellos
// ("nostalgia"). No son vehículos: se dibujan aparte (ClassicV4Marker).
export const LEGACY_MARKERS = [{ id: "clasico-v4", name: "Clásico v4" }] as const;
export type LegacyMarkerId = (typeof LEGACY_MARKERS)[number]["id"];
export const isLegacyMarker = (id: string | null | undefined): id is LegacyMarkerId =>
  LEGACY_MARKERS.some((m) => m.id === id);

// Largos de referencia: auto compacto 4.2 m, pickup doble cabina 5.6 m,
// camión 3.5 t 6 m, rabón/thorton 8.5 m, tracto 7 m, retroexcavadora 7 m.
// Tracto + remolque: 14 m, NO los ~21 m reales (tracto + caja de 53 ft): a
// escala real medía el triple que un tracto (78 px en zoom 19) y tapaba a
// las demás unidades (Daniel, 6-oct-2026). Al doble se sigue leyendo largo.
export const VEHICLE_MODELS: VehicleModel[] = [
  { id: "tracto-blanco", name: "Tracto blanco", category: "tracto", lengthM: 7, aspect: 2.353, rear: true },
  { id: "tracto-negro", name: "Tracto negro", category: "tracto", lengthM: 7, aspect: 2.341, rear: true },
  { id: "tracto-rojo", name: "Tracto rojo", category: "tracto", lengthM: 7, aspect: 2.341, rear: true },
  { id: "tracto-azul", name: "Tracto azul", category: "tracto", lengthM: 7, aspect: 2.341, rear: true },
  { id: "tracto-amarillo", name: "Tracto amarillo", category: "tracto", lengthM: 7, aspect: 2.353, rear: true },
  { id: "tracto-dumax", name: "Tracto Dumax", category: "tracto", lengthM: 7, aspect: 2.526, rear: true },
  { id: "tracto-blanco-aceros", name: "Tracto blanco · plataforma", category: "tracto-remolque", lengthM: 14, aspect: 4.752, rear: true },
  { id: "tracto-negro-caja-blanca", name: "Tracto negro · caja seca", category: "tracto-remolque", lengthM: 14, aspect: 3.84, rear: true },
  { id: "tracto-rojo-pipa-gas", name: "Tracto rojo · pipa de gas", category: "tracto-remolque", lengthM: 14, aspect: 3.664, rear: true },
  { id: "tracto-azul-caja-azul", name: "Tracto azul · caja seca", category: "tracto-remolque", lengthM: 14, aspect: 5.053, rear: true },
  { id: "tracto-amarillo-pipa", name: "Tracto amarillo · pipa", category: "tracto-remolque", lengthM: 14, aspect: 4.248, rear: true },
  { id: "tracto-dumax-caja-dumax", name: "Tracto Dumax · caja Dumax", category: "tracto-remolque", lengthM: 14, aspect: 4.248, rear: true },
  { id: "camion-35-amarillo", name: "Camión 3.5 t", category: "camion", lengthM: 6, aspect: 2.162, rear: true },
  { id: "thorton-verde", name: "Thorton", category: "camion", lengthM: 8.5, aspect: 2.162, rear: true },
  { id: "pickup-blanca", name: "Pickup blanca", category: "pickup", lengthM: 5.6, aspect: 2.162, rear: true },
  { id: "pickup-negra", name: "Pickup negra", category: "pickup", lengthM: 5.6, aspect: 2.133, rear: true },
  { id: "auto-blanco", name: "Auto blanco", category: "auto", lengthM: 4.2, aspect: 2.034, rear: true },
  { id: "auto-negro", name: "Auto negro", category: "auto", lengthM: 4.2, aspect: 2.124, rear: true },
  { id: "retroexcavadora", name: "Retroexcavadora", category: "maquinaria", lengthM: 7, aspect: 2.712, rear: true },
  // Nostalgia · Dumax v2: el remaster y, con sufijo -8bit, el original.
  ...v2("v2-taxi", "Taxi", 4.6, 2.008, 1.875),
  ...v2("v2-van", "Van", 5.5, 2.424, 2.143),
  ...v2("v2-auto-amarillo", "Auto amarillo", 4.5, 2.124, 1.917),
  ...v2("v2-auto-cafe", "Auto café", 4.7, 1.943, 2.091),
  ...v2("v2-auto-verde", "Auto verde", 4.4, 2.034, 2.091),
  ...v2("v2-suv-azul", "SUV azul", 4.7, 2, 1.917),
  ...v2("v2-thorton", "Thorton v2", 8.5, 2, 2.3),
];

function v2(id: string, name: string, lengthM: number, aspect: number, aspect8bit: number): VehicleModel[] {
  return [
    { id, name, category: "nostalgia", lengthM, aspect },
    { id: `${id}-8bit`, name: `${name} · 8-bit`, category: "nostalgia", lengthM, aspect: aspect8bit, pixel: true },
  ];
}
// Sufijo de la versión 8-bit (original de v2) de un modelo.
export const BIT8_SUFFIX = "-8bit";

const BY_ID = new Map(VEHICLE_MODELS.map((m) => [m.id, m]));

export const modelTopUrl = (id: string) => `/vehicles/${id}/top.webp`;
export const modelSideUrl = (id: string) => `/vehicles/${id}/side.webp`;
export const modelRearUrl = (id: string) => `/vehicles/${id}/rear.webp`;

// Por omisión, según la carrocería de Administración (vehicle.type), un modelo
// neutro (blanco) de su categoría. Lo que no se reconoce cae en tracto: es la
// mayoría de las flotas.
const DEFAULT_BY_BODY: Record<string, string> = {
  truck: "tracto-blanco",
  pickup: "pickup-blanca",
  car: "auto-blanco",
  sedan: "auto-blanco",
  hatchback: "auto-blanco",
  coupe: "auto-blanco",
  convertible: "auto-blanco",
  wagon: "auto-blanco",
  suv: "auto-blanco",
  mpv: "auto-blanco",
  van: "auto-blanco",
};

/**
 * Qué dibujar para una unidad: un modelo, o null = marcador clásico.
 * - elegido explícitamente: ése (o clásico si eligió "Clásico");
 * - si no, el de su carrocería;
 * - los remolques sueltos se quedan en clásico: no hay modelo de remolque solo.
 */
export function resolveVehicleModel(
  pointerModel: string | null | undefined,
  bodyType: string | null | undefined,
  unitType?: string | null,
): VehicleModel | null {
  if (pointerModel === CLASSIC_MODEL_ID || isLegacyMarker(pointerModel)) return null;
  if (pointerModel && BY_ID.has(pointerModel)) return BY_ID.get(pointerModel)!;
  if (unitType === "remolque" || bodyType === "trailer") return null;
  return BY_ID.get(DEFAULT_BY_BODY[(bodyType || "").toLowerCase()] ?? "tracto-blanco") ?? null;
}

/**
 * Largo en pantalla (px): su TAMAÑO REAL a la escala del mapa, como se vería
 * en una foto satelital (al acercar se ve más grande, al alejar más chico,
 * igual que la calle). Sólo un piso de 12 px para que de lejos siga siendo
 * visible y tocable: con 10·√largo (tracto 26 px) la unidad "crecía" al
 * alejar, que es justo lo que no pasa en la realidad (Daniel, 6-oct-2026).
 * El mapa topa en zoom 19 (MapView maxZoom): ahí un tracto mide ~26 px y un
 * tráiler ~78, así nunca se agranda de más.
 */
export function pointerLengthPx(lengthM: number, zoom: number, lat: number): number {
  const metersPerPx = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
  const real = metersPerPx > 0 ? lengthM / metersPerPx : 0;
  return Math.round(Math.min(POINTER_MAX_PX, Math.max(POINTER_MIN_PX, real)));
}
export const POINTER_MIN_PX = 12;
export const POINTER_MAX_PX = 160;
